import { useEffect, useState } from 'react'
import ConfirmDialog from '../components/ui/ConfirmDialog'
import DataTable from '../components/ui/DataTable'
import EmptyState from '../components/ui/EmptyState'
import LoadingState from '../components/ui/LoadingState'
import PageHeader from '../components/ui/PageHeader'
import PageShell from '../components/ui/PageShell'
import SectionCard from '../components/ui/SectionCard'
import Button from '../components/ui/Button'
import llmCacheService from '../services/llmCacheService'
import { useTranslation } from '../contexts/TranslationContext'
import { ui } from '../styles/ui'
import { formatEmptyValue } from '../utils/displayValue'

export default function LLMCacheAdmin() {
  const [stats, setStats] = useState(null)
  const [items, setItems] = useState([])
  const [page, setPage] = useState(1)
  const [perPage] = useState(25)
  const [loading, setLoading] = useState(false)
  const [confirmClear, setConfirmClear] = useState(false)
  const { t } = useTranslation()

  useEffect(() => {
    loadStats()
    loadList(page)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page])

  function loadStats() {
    llmCacheService.stats().then((res) => setStats(res.data)).catch(() => setStats(null))
  }

  function loadList(p = 1) {
    setLoading(true)
    llmCacheService.list(p, perPage).then((res) => {
      setItems(res.data.items || [])
      setLoading(false)
    }).catch(() => setLoading(false))
  }

  function invalidate(key) {
    if (!confirm(`${t('Are you sure?')}`)) return
    llmCacheService.invalidate(key).then(() => {
      loadStats(); loadList(page)
    })
  }

  function clearAll() {
    setConfirmClear(false)
    llmCacheService.clearAll().then(() => {
      loadStats(); loadList(1); setPage(1)
    })
  }

  return (
    <PageShell>
      <PageHeader
        title={t('LLM Image Queue Cache')}
        actions={<Button variant="danger" onClick={() => setConfirmClear(true)}>{t('Clear cache')}</Button>}
      />

      <SectionCard>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Driver</p>
            <p className="text-lg font-semibold text-foreground">{formatEmptyValue(stats?.driver)}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Cached items</p>
            <p className="text-lg font-semibold text-foreground">{formatEmptyValue(stats?.count)}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">TTL (s)</p>
            <p className="text-lg font-semibold text-foreground">{formatEmptyValue(stats?.ttl_seconds)}</p>
          </div>
        </div>
      </SectionCard>

      <SectionCard title={t('Cached items')}>
        {loading ? <LoadingState text={t('Loading...', 'Loading...')} /> : (
          items.length === 0 ? <EmptyState title={t('No cached items')} /> : (
            <DataTable
              columns={[
                { key: 'key', label: 'Key', sortable: true },
                { key: 'image_path', label: 'Image path', sortable: true },
                { key: 'cached_at', label: 'Cached at', sortable: true, sortAccessor: (item) => item.cached_at || '' },
                { key: 'provider', label: 'Provider', sortable: true },
                { key: 'actions', label: '' },
              ]}
              rows={items}
              renderRow={(it) => (
                <tr key={it.key} className={ui.tableRow}>
                  <td className={ui.tableCell}><span className="text-xs text-muted-foreground break-all">{it.key}</span></td>
                  <td className={ui.tableCellText}>{formatEmptyValue(it.image_path)}</td>
                  <td className={ui.tableCellText}>{formatEmptyValue(it.cached_at)}</td>
                  <td className={ui.tableCellText}>{formatEmptyValue(it.provider)}</td>
                  <td className={ui.tableCell}><Button size="sm" variant="danger" onClick={() => invalidate(it.key)}>{t('Invalidate')}</Button></td>
                </tr>
              )}
            />
          )
        )}

        {items.length > 0 && (
          <div className="mt-4 flex items-center gap-3">
            <Button variant="ghost" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Previous</Button>
            <span className="text-sm text-muted-foreground">{t('Page')} {page}</span>
            <Button variant="ghost" disabled={items.length < perPage} onClick={() => setPage((p) => p + 1)}>Next</Button>
          </div>
        )}
      </SectionCard>

      <ConfirmDialog open={confirmClear} onClose={() => setConfirmClear(false)} onConfirm={clearAll} title={t('Clear LLM cache')}>
        {t('Are you sure you want to clear the entire LLM image queue cache? This cannot be undone.')}
      </ConfirmDialog>
    </PageShell>
  )
}


